import * as THREE from 'three';
import type { InteractTarget } from '../interact/Interact';
import { CC_NODES, CC_PALMS, CC_SPAWN } from '../world/areas/a01/CrashCoast';

const CABIN = CC_NODES['CC-CABIN'] ?? { x: -8, y: 1.6, z: -22 };
const SPAWN = CC_SPAWN;

/** Driftwood on the sand beside the cockpit wreck. */
export const NODE_DRIFTWOOD = { x: SPAWN.x + 1.85, y: 0.14, z: SPAWN.z + 0.35 };
/** Fiber tuft on the cabin's west fringe. */
export const NODE_FIBER = { x: CABIN.x - 2.2, y: 0.22, z: CABIN.z + 1.8 };
/** Rebar spear leaning on the cabin east face. */
export const NODE_SPEAR = { x: CABIN.x + 1.45, y: 0.9, z: CABIN.z };
export const FIBER_Y = 0.22;
export const WOOD_Y = 0.14;

export type InlandSpec = {
  id: string;
  x: number;
  z: number;
  qty: number;
  prompt: string;
};

/**
 * Extra inland fiber. Cabin tuft is qty 2; lean-to wants 4, zodiac 4, bow 2.
 * Jungle ferns, Glassvein reeds, and swamp reeds sit on dressing marks.
 * Marks stay off cabin / Ember pad / spear / hut / zodiac / tea pool so locked E / F paths hold.
 */
export const INLAND_FIBERS: readonly InlandSpec[] = [
  { id: 'CC-FIBER-02', x: -12.4, z: -31.6, qty: 2, prompt: 'E — Fiber' },
  { id: 'CC-FIBER-03', x: -11.2, z: -62.4, qty: 2, prompt: 'E — Fiber' },
  { id: 'DJ-FIBER-01', x: -11, z: -76, qty: 2, prompt: 'E — Fiber' },
  { id: 'DJ-FIBER-02', x: -13, z: -100, qty: 2, prompt: 'E — Fiber' },
  { id: 'DJ-FIBER-03', x: 3, z: -80, qty: 2, prompt: 'E — Fiber' },
  { id: 'DJ-FIBER-04', x: 5, z: -94, qty: 2, prompt: 'E — Fiber' },
  { id: 'DJ-FIBER-05', x: 3, z: -120, qty: 2, prompt: 'E — Fiber' },
  { id: 'RL-FIBER-01', x: 58, z: 6.4, qty: 2, prompt: 'E — Reed fiber' },
  { id: 'RL-FIBER-02', x: 70, z: 4.8, qty: 2, prompt: 'E — Reed fiber' },
  { id: 'RL-FIBER-03', x: 50, z: -12.5, qty: 2, prompt: 'E — Reed fiber' },
  { id: 'RL-FIBER-04', x: 64, z: -16.2, qty: 2, prompt: 'E — Reed fiber' },
  { id: 'SB-FIBER-01', x: -64, z: -38, qty: 2, prompt: 'E — Reed fiber' },
  { id: 'SB-FIBER-02', x: -90, z: -36, qty: 2, prompt: 'E — Reed fiber' },
];

/**
 * Jungle logs + Glassvein drift. Palms stay coast-only; inland wood funds zodiac
 * without walking back to Crash Coast. Same keep-off as inland fiber.
 */
export const INLAND_WOODS: readonly InlandSpec[] = [
  { id: 'DJ-WOOD-01', x: -18, z: -90, qty: 2, prompt: 'E — Jungle log' },
  { id: 'DJ-WOOD-02', x: 14, z: -106, qty: 2, prompt: 'E — Jungle log' },
  { id: 'DJ-WOOD-03', x: 18, z: -84, qty: 2, prompt: 'E — Jungle log' },
  { id: 'RL-WOOD-01', x: 58, z: -13.6, qty: 2, prompt: 'E — River drift' },
  { id: 'RL-WOOD-02', x: 96, z: 5.2, qty: 2, prompt: 'E — River drift' },
  { id: 'RL-WOOD-03', x: 74, z: 6.8, qty: 2, prompt: 'E — River drift' },
];

export type NodeKind = 'wood' | 'fiber' | 'spear' | 'raw';

export type GatherNode = InteractTarget & {
  kind: NodeKind;
  item: string;
  qty: number;
  mesh: THREE.Object3D;
  take(): boolean;
};

export function spawnCrashCoastNodes(scene: THREE.Scene): GatherNode[] {
  const root = new THREE.Group();
  root.name = 'CC-PICKUPS';
  root.userData.id = 'CC-PICKUPS';
  scene.add(root);

  const driftwood = makeNode({
    id: 'CC-DRIFTWOOD',
    kind: 'wood',
    item: 'wood',
    qty: 3,
    prompt: 'E — Driftwood',
    position: NODE_DRIFTWOOD,
    radius: 0.55,
    mesh: makeDriftwood(),
  });
  const driftwood2 = makeNode({
    id: 'CC-DRIFTWOOD-02',
    kind: 'wood',
    item: 'wood',
    qty: 2,
    prompt: 'E — Wreck plank',
    position: { x: SPAWN.x - 2.1, y: 0.14, z: SPAWN.z + 1.1 },
    radius: 0.55,
    mesh: makeDriftwood(),
  });
  const fiber = makeNode({
    id: 'CC-FIBER',
    kind: 'fiber',
    item: 'fiber',
    qty: 2,
    prompt: 'E — Fiber',
    position: NODE_FIBER,
    radius: 0.4,
    mesh: makeFiber(),
  });
  const spearMesh = makeRebarSpear();
  const spear = makeNode({
    id: 'CC-SPEAR',
    kind: 'spear',
    item: 'spear',
    qty: 1,
    prompt: 'E — Improvised spear',
    position: NODE_SPEAR,
    radius: 0.45,
    mesh: spearMesh,
  });
  const cabin = makeNode({
    id: 'CC-CABIN',
    kind: 'spear',
    item: 'spear',
    qty: 1,
    prompt: 'E — Improvised spear',
    position: CABIN,
    radius: 2.4,
    mesh: new THREE.Group(),
    onTake: () => spear.take(),
  });

  const inland: GatherNode[] = [];
  for (const spec of INLAND_FIBERS) {
    const node = makeNode({
      id: spec.id,
      kind: 'fiber',
      item: 'fiber',
      qty: spec.qty,
      prompt: spec.prompt,
      position: { x: spec.x, y: FIBER_Y, z: spec.z },
      radius: 0.4,
      mesh: makeFiber(),
    });
    inland.push(node);
    root.add(node.mesh);
  }

  const inlandWood: GatherNode[] = [];
  for (const spec of INLAND_WOODS) {
    const node = makeNode({
      id: spec.id,
      kind: 'wood',
      item: 'wood',
      qty: spec.qty,
      prompt: spec.prompt,
      position: { x: spec.x, y: WOOD_Y, z: spec.z },
      radius: 0.55,
      mesh: makeDriftwood(),
    });
    inlandWood.push(node);
    root.add(node.mesh);
  }

  const palms: GatherNode[] = [];
  for (let i = 0; i < CC_PALMS.length; i++) {
    const palm = CC_PALMS[i];
    if (!palm) {
      continue;
    }
    const node = makeNode({
      id: `CC-PALM-${String(i + 1).padStart(2, '0')}`,
      kind: 'wood',
      item: 'wood',
      qty: 2,
      prompt: 'E — Strip palm',
      position: { x: palm.x, y: 0.8, z: palm.z },
      radius: 1.15,
      mesh: makePalmMark(),
    });
    palms.push(node);
    root.add(node.mesh);
  }

  root.add(driftwood.mesh, driftwood2.mesh, fiber.mesh, spear.mesh);
  return [driftwood, driftwood2, fiber, spear, cabin, ...inland, ...inlandWood, ...palms];
}

export function makeMeatDrop(pos: { x: number; y: number; z: number }, qty: number, id: string): GatherNode {
  const slab = new THREE.Group();
  const flesh = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.07, 0.16),
    new THREE.MeshStandardMaterial({ color: 0x8a3a32, roughness: 0.72, metalness: 0.04 }),
  );
  const fat = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.04, 0.08),
    new THREE.MeshStandardMaterial({ color: 0xc8a878, roughness: 0.55, metalness: 0.02 }),
  );
  fat.position.set(0.04, 0.04, 0);
  slab.add(flesh, fat);
  slab.castShadow = true;
  return makeNode({
    id,
    kind: 'raw',
    item: 'raw',
    qty,
    prompt: qty > 1 ? `E — Raw meat ×${qty}` : 'E — Raw meat',
    position: { x: pos.x, y: pos.y + 0.08, z: pos.z },
    radius: 0.55,
    mesh: slab,
  });
}

function makeNode(opts: {
  id: string;
  kind: NodeKind;
  item: string;
  qty: number;
  prompt: string;
  position: { x: number; y: number; z: number };
  radius: number;
  mesh: THREE.Object3D;
  onTake?: () => boolean;
}): GatherNode {
  opts.mesh.name = opts.id;
  opts.mesh.userData.id = opts.id;
  opts.mesh.position.set(opts.position.x, opts.position.y, opts.position.z);
  const node: GatherNode = {
    id: opts.id,
    kind: opts.kind,
    item: opts.item,
    qty: opts.qty,
    prompt: opts.prompt,
    position: opts.position,
    radius: opts.radius,
    live: true,
    mesh: opts.mesh,
    take(): boolean {
      if (!node.live) {
        return false;
      }
      node.live = false;
      node.mesh.visible = false;
      opts.onTake?.();
      return true;
    },
  };
  return node;
}

function makeDriftwood(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(1.55, 0.22, 0.36),
    new THREE.MeshStandardMaterial({
      color: 0x8a6a48,
      roughness: 0.92,
      metalness: 0.02,
      emissive: 0x2a1c10,
      emissiveIntensity: 0.18,
    }),
  );
  mesh.rotation.y = 0.42;
  mesh.castShadow = true;
  return mesh;
}

function makePalmMark(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.18, 0.35, 6),
    new THREE.MeshStandardMaterial({
      color: 0xc4a06a,
      roughness: 0.7,
      metalness: 0.04,
      emissive: 0x3a2810,
      emissiveIntensity: 0.22,
    }),
  );
  mesh.position.y = 0.2;
  return mesh;
}

function makeFiber(): THREE.Group {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0x4a5a32, roughness: 0.9, metalness: 0.02 });
  for (const [x, z, h, tilt] of [
    [0, 0, 0.55, 0.12],
    [0.08, 0.05, 0.42, -0.18],
    [-0.07, 0.04, 0.48, 0.22],
  ] as const) {
    const blade = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.03, h, 5), mat);
    blade.position.set(x, h * 0.5, z);
    blade.rotation.z = tilt;
    group.add(blade);
  }
  return group;
}

function makeRebarSpear(): THREE.Group {
  const group = new THREE.Group();
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(0.022, 0.028, 1.55, 6),
    new THREE.MeshStandardMaterial({ color: 0x6a4030, roughness: 0.7, metalness: 0.38 }),
  );
  shaft.rotation.z = 0.38;
  const tip = new THREE.Mesh(
    new THREE.ConeGeometry(0.04, 0.16, 5),
    new THREE.MeshStandardMaterial({ color: 0x8a8f88, roughness: 0.42, metalness: 0.58 }),
  );
  tip.position.set(0.24, 0.76, 0);
  tip.rotation.z = 0.38;
  group.add(shaft, tip);
  return group;
}
